/**
 * Markdown-Based Wiki System JavaScript
 * Handles wiki navigation, search, and dynamic content loading from markdown files
 * with fallback to Ghost posts
 */

class MarkdownWikiSystem {
    constructor() {
        this.config = window.wikiConfig || {};
        this.wikiPages = [];
        this.currentPage = null;
        this.searchIndex = [];
        this.dataSource = 'markdown'; // 'markdown' or 'ghost'
        this.seoScriptIds = {
            article: 'wiki-article-jsonld',
            breadcrumbs: 'wiki-breadcrumbs-jsonld'
        };
        
        this.init();
    }

    async init() {
        await this.loadWikiData();
        this.setupEventListeners();
        this.renderNavigation();
        this.setupSearch();
        
        // Default to current pathname if template didn't provide a url
        const effectiveUrl = this.config.currentPage && this.config.currentPage !== ''
            ? this.config.currentPage
            : (typeof window !== 'undefined' ? window.location.pathname : '');
        if (effectiveUrl) {
            this.config.currentPage = effectiveUrl;
            this.setCurrentPage(effectiveUrl);
        }
    }

    async loadWikiData() {
        try {
            // Try to load from markdown data first
            const response = await fetch(this.config.dataUrl);
            if (response.ok) {
                const data = await response.json();
                this.wikiPages = data.pages || [];
                this.dataSource = 'markdown';
            } else {
                throw new Error('Markdown data not available');
            }
        } catch (error) {
            
            
            // Fallback to Ghost API
            if (this.config.fallbackToGhost) {
                await this.loadFromGhostAPI();
            } else {
                this.wikiPages = [];
            }
        }
        
        this.buildSearchIndex();
    }

    async loadFromGhostAPI() {
        try {
            const response = await fetch(`${this.config.ghostApiUrl}?key=${this.config.ghostApiKey}&filter=tag:wiki&include=tags&limit=all`);
            const data = await response.json();
            
            this.wikiPages = data.posts || [];
            this.dataSource = 'ghost';
            
        } catch (error) {
            console.error('Failed to load wiki pages from Ghost API:', error);
            this.wikiPages = [];
        }
    }

    buildSearchIndex() {
        this.searchIndex = this.wikiPages.map(page => ({
            id: page.id,
            title: page.title,
            excerpt: page.excerpt,
            content: page.content || page.html,
            tags: page.tags || [],
            url: page.url,
            published_at: page.published_at,
            category: page.category
        }));
    }

    setupEventListeners() {
        // Search functionality
        const searchInput = document.getElementById('wiki-search');
        const searchBtn = document.getElementById('wiki-search-btn');
        
        if (searchInput) {
            searchInput.addEventListener('input', (e) => this.handleSearch(e.target.value));
            searchInput.addEventListener('keypress', (e) => {
                if (e.key === 'Enter') {
                    this.performSearch(e.target.value);
                }
            });
        }
        
        if (searchBtn) {
            searchBtn.addEventListener('click', () => {
                const query = searchInput?.value || '';
                this.performSearch(query);
            });
        }
    }

    renderNavigation() { this.renderTreeNavigation(); }

    renderTreeNavigation() {
        const navElement = document.getElementById('wiki-nav');
        if (!navElement) return;

        const tree = this.buildWikiTree(this.wikiPages);
        navElement.innerHTML = this.renderTreeHTML(tree, 0, []);

        // Expand nodes to current page
        this.expandToCurrentPage(navElement);

        // Toggle behavior
        navElement.addEventListener('click', (e) => {
            const toggle = e.target.closest('.wiki-tree-toggle');
            if (!toggle) return;
            const li = toggle.closest('li');
            const child = li && li.querySelector(':scope > ul');
            if (!child) return;
            const expanded = toggle.getAttribute('aria-expanded') === 'true';
            const newExpanded = !expanded;
            toggle.setAttribute('aria-expanded', newExpanded ? 'true' : 'false');
            child.style.display = newExpanded ? 'block' : 'none';
            try { toggle.textContent = newExpanded ? '▾' : '▸'; } catch {}
        });
    }

    buildWikiTree(pages) {
        const root = { __type: 'root', children: new Map(), pages: [] };
        const getOrCreate = (parent, segment) => {
            if (!parent.children.has(segment)) {
                parent.children.set(segment, { __type: 'group', name: segment, children: new Map(), pages: [] });
            }
            return parent.children.get(segment);
        };
        const toSegments = (url) => {
            const base = (this.config.baseUrl || '');
            const abs = url.startsWith('http') ? url.replace(base, '') : url;
            const trimmed = abs.replace(/^\/?/, '/').replace(/\/$/, '/').replace(/^\/wiki\//, '').replace(/\/$/, '');
            return trimmed ? trimmed.split('/') : [];
        };
        pages.forEach((page) => {
            const segments = toSegments(page.url);
            let cursor = root;
            for (let i = 0; i < segments.length; i += 1) {
                const segment = segments[i];
                if (i === segments.length - 1) {
                    cursor.pages.push(page);
                } else {
                    cursor = getOrCreate(cursor, segment);
                }
            }
            if (segments.length === 0) root.pages.push(page);
        });
        return root;
    }

    renderTreeHTML(node, depth = 0, pathSegments = []) {
        const sortByName = (a, b) => a[0].localeCompare(b[0]);
        const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

        const parts = [];
        const ulStyle = depth === 0 ? '' : ' style="display:none"';
        parts.push(`<ul class="wiki-tree depth-${depth}"${ulStyle}>`);

        // Render group children first
        const groups = Array.from(node.children?.entries?.() || []).sort(sortByName);
        groups.forEach(([segment, child]) => {
            const label = esc(segment.replace(/-/g, ' '));
            const expanded = false;
            const toggleChar = expanded ? '▾' : '▸';
            const accumulated = [...pathSegments, segment];
            const tagPath = accumulated.map(encodeURIComponent).join('/');
            const rawChildren = this.renderTreeHTML(child, depth + 1, accumulated);
            const childrenHTML = expanded ? rawChildren : rawChildren.replace('<ul ', '<ul style="display:none" ');
            parts.push(
                `<li class="wiki-tree-group">
                    <button class="wiki-tree-toggle" aria-expanded="${expanded ? 'true' : 'false'}" aria-label="Toggle ${label}">${toggleChar}</button>
                    <span class="wiki-tree-label"><a href="${(this.config.baseUrl || '')}/wiki/tag/${tagPath}/">${label}</a></span>
                    ${childrenHTML}
                </li>`
            );
        });

        // Then render pages attached at this level (as leaves)
        const leaves = (node.pages || []).slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        leaves.forEach((page) => {
            const isActive = this.config.currentPage === page.url;
            parts.push(
                `<li class="wiki-tree-leaf">
                    <a href="${page.url}" class="wiki-tree-link ${isActive ? 'active' : ''}">${esc(page.title || page.slug || page.url)}</a>
                </li>`
            );
        });

        parts.push('</ul>');
        return parts.join('');
    }

    expandToCurrentPage(navElement) {
        const current = this.config.currentPage;
        if (!current) return;
        const active = navElement.querySelector(`a[href="${current}"]`);
        if (!active) return;
        let node = active.parentElement;
        while (node && node !== navElement) {
            if (node.tagName === 'UL') {
                node.style.display = 'block';
            }
            if (node.classList && node.classList.contains('wiki-tree-group')) {
                const toggle = node.querySelector(':scope > .wiki-tree-toggle');
                const child = node.querySelector(':scope > ul');
                if (toggle && child) {
                    toggle.setAttribute('aria-expanded', 'true');
                    child.style.display = 'block';
                }
            }
            node = node.parentElement;
        }
    }

    groupPagesByCategory() {
        const categories = {};
        
        this.wikiPages.forEach(page => {
            const category = page.category || 'Uncategorized';
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(page);
        });
        
        return categories;
    }

    setupSearch() {
        this.searchInput = document.getElementById('wiki-search');
        this.searchResults = [];
    }

    handleSearch(query) {
        if (!query.trim()) {
            this.clearSearchResults();
            return;
        }

        const results = this.searchPages(query);
        this.displaySearchResults(results);
    }

    performSearch(query) {
        if (!query.trim()) return;
        
        const results = this.searchPages(query);
        if (results.length > 0) {
            window.location.href = results[0].url;
        }
    }

    searchPages(query) {
        const searchTerm = query.toLowerCase();
        
        return this.searchIndex.filter(page => {
            return page.title.toLowerCase().includes(searchTerm) ||
                   page.excerpt.toLowerCase().includes(searchTerm) ||
                   page.content.toLowerCase().includes(searchTerm) ||
                   page.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
                   page.category.toLowerCase().includes(searchTerm);
        }).sort((a, b) => {
            // Prioritize title matches
            const aTitleMatch = a.title.toLowerCase().includes(searchTerm);
            const bTitleMatch = b.title.toLowerCase().includes(searchTerm);
            
            if (aTitleMatch && !bTitleMatch) return -1;
            if (!aTitleMatch && bTitleMatch) return 1;
            
            return new Date(b.published_at) - new Date(a.published_at);
        });
    }

    displaySearchResults(results) {
        this.highlightSearchResults(results);
    }

    highlightSearchResults(results) {
        // Remove existing highlights
        document.querySelectorAll('.wiki-nav-link').forEach(link => {
            link.classList.remove('search-highlight');
        });
        
        // Add highlights to matching pages
        results.forEach(result => {
            const link = document.querySelector(`.wiki-nav-link[href="${result.url}"]`);
            if (link) {
                link.classList.add('search-highlight');
            }
        });
    }

    clearSearchResults() {
        document.querySelectorAll('.wiki-nav-link').forEach(link => {
            link.classList.remove('search-highlight');
        });
    }

    setCurrentPage(url) {
        this.currentPage = url;
        this.loadPageContent();
        this.updateBreadcrumb();
        this.updateNavigation();
        this.updatePrevNextLinks();
    }

    async loadPageContent() {
        // Resolve canonical page by url or alias
        let page = this.wikiPages.find(p => p.url === this.currentPage);
        if (!page) {
            page = this.wikiPages.find(p => Array.isArray(p.aliases) && p.aliases.includes(this.currentPage));
            if (page) {
                // Normalize to canonical URL for navigation highlighting
                this.currentPage = page.url;
            }
        }
        if (!page) {
            this.showPageNotFound();
            return;
        }

        // Update page title
        const titleElement = document.getElementById('wiki-title');
        if (titleElement) {
            titleElement.textContent = page.title;
        }

        // Update feature image
        const featureImageElement = document.getElementById('wiki-feature-image');
        if (featureImageElement && page.feature_image) {
            featureImageElement.innerHTML = `<img src="${page.feature_image}" alt="${page.title}" />`;
            featureImageElement.style.display = 'block';
        } else if (featureImageElement) {
            featureImageElement.style.display = 'none';
        }

        // Update content
        const contentElement = document.getElementById('wiki-content');
        if (contentElement) {
            if (this.dataSource === 'markdown') {
                // For markdown files, content is already HTML
                contentElement.innerHTML = page.html;
            } else {
                // For Ghost posts, content might need processing
                contentElement.innerHTML = page.html || page.content;
            }

            // Ensure title is only rendered once: remove first H1 from article body
            try {
                const firstHeading = contentElement.querySelector('h1');
                if (firstHeading) firstHeading.remove();
            } catch {}
        }

        // Update meta information
        this.updateMetaInfo(page);

        // Update SEO meta tags & JSON-LD
        this.updateSeoForPage(page);
    }

    updateMetaInfo(page) {
        // Update last updated
        const lastUpdatedElement = document.getElementById('wiki-last-updated');
        if (lastUpdatedElement) {
            const date = new Date(page.updated_at || page.published_at);
            lastUpdatedElement.textContent = `Last updated: ${date.toLocaleDateString()}`;
        }

        // Update tags
        const tagsElement = document.getElementById('wiki-tags');
        if (tagsElement && page.tags && page.tags.length > 0) {
            const baseUrl = (this.config.baseUrl || '').replace(/\/$/, '');
            tagsElement.innerHTML = page.tags.map(tag => 
                `<a class="wiki-tag" href="${baseUrl}/wiki/tag/${encodeURIComponent(tag)}/">${tag}</a>`
            ).join('');
        } else if (tagsElement) {
            tagsElement.innerHTML = '';
        }
    }

    // ---------- SEO Utilities ----------
    setMetaTag(nameOrProperty, content, { property = false } = {}) {
        if (!content) return;
        const selector = property ? `meta[property="${nameOrProperty}"]` : `meta[name="${nameOrProperty}"]`;
        let tag = document.head.querySelector(selector);
        if (!tag) {
            tag = document.createElement('meta');
            if (property) tag.setAttribute('property', nameOrProperty); else tag.setAttribute('name', nameOrProperty);
            document.head.appendChild(tag);
        }
        tag.setAttribute('content', content);
    }

    setLinkTag(rel, href) {
        if (!href) return;
        let link = document.head.querySelector(`link[rel="${rel}"]`);
        if (!link) {
            link = document.createElement('link');
            link.setAttribute('rel', rel);
            document.head.appendChild(link);
        }
        link.setAttribute('href', href);
    }

    appendMetaProperty(propertyName, content) {
        if (!content) return;
        const tag = document.createElement('meta');
        tag.setAttribute('property', propertyName);
        tag.setAttribute('content', content);
        document.head.appendChild(tag);
    }

    injectJsonLd(id, data) {
        if (!data) return;
        let script = document.getElementById(id);
        if (!script) {
            script = document.createElement('script');
            script.type = 'application/ld+json';
            script.id = id;
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(data);
    }

    toAbsoluteUrl(url) {
        if (!url) return '';
        if (url.startsWith('http://') || url.startsWith('https://')) return url;
        const base = (this.config.baseUrl || '').replace(/\/$/, '');
        const path = url.startsWith('/') ? url : `/${url}`;
        return `${base}${path}`;
    }

    buildBreadcrumbJsonLd(page) {
        const siteUrl = (this.config.baseUrl || '').replace(/\/$/, '');
        const absoluteUrl = this.toAbsoluteUrl(page.url);
        const category = page.category || (Array.isArray(page.tags) && page.tags[0]) || 'Wiki';
        const items = [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: 'Wiki', item: `${siteUrl}/wiki/` }
        ];
        if (category && category !== 'Wiki') {
            items.push({ '@type': 'ListItem', position: 3, name: category, item: `${siteUrl}/wiki/tag/${encodeURIComponent(category)}/` });
            items.push({ '@type': 'ListItem', position: 4, name: page.title, item: absoluteUrl });
        } else {
            items.push({ '@type': 'ListItem', position: 3, name: page.title, item: absoluteUrl });
        }
        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items
        };
    }

    updateSeoForPage(page) {
        if (!page) return;
        const siteTitle = this.config.siteTitle || '';
        const siteLocale = this.config.siteLocale || 'en';
        const pageTitle = page.title ? `${page.title} | ${siteTitle}` : siteTitle;
        const rawDescription = page.meta_description || page.excerpt || (page.content || '').replace(/<[^>]+>/g, '').slice(0, 300);
        const description = rawDescription ? rawDescription.trim().replace(/\s+/g, ' ').slice(0, 160) : '';
        const url = this.toAbsoluteUrl(page.url);
        const image = this.toAbsoluteUrl(page.feature_image);
        const keywords = Array.isArray(page.tags) ? page.tags.join(', ') : '';
        const siteTwitter = (this.config.siteTwitter || '').toString().replace(/^@/, '');
        const ogLocale = siteLocale ? siteLocale.replace('-', '_') : undefined;
        const publishedISO = page.published_at ? new Date(page.published_at).toISOString() : undefined;
        const modifiedISO = page.updated_at ? new Date(page.updated_at).toISOString() : undefined;

        // Title
        if (pageTitle) document.title = pageTitle;

        // Canonical
        this.setLinkTag('canonical', url);

        // Meta name
        this.setMetaTag('description', description);
        this.setMetaTag('keywords', keywords);

        // OpenGraph
        this.setMetaTag('og:title', page.title || siteTitle, { property: true });
        this.setMetaTag('og:description', description, { property: true });
        this.setMetaTag('og:type', 'article', { property: true });
        this.setMetaTag('og:url', url, { property: true });
        if (image) this.setMetaTag('og:image', image, { property: true });
        if (siteTitle) this.setMetaTag('og:site_name', siteTitle, { property: true });
        if (ogLocale) this.setMetaTag('og:locale', ogLocale, { property: true });
        if (publishedISO) this.setMetaTag('article:published_time', publishedISO, { property: true });
        if (modifiedISO) this.setMetaTag('article:modified_time', modifiedISO, { property: true });
        try {
            document.head.querySelectorAll('meta[property="article:tag"]').forEach(n => n.parentNode && n.parentNode.removeChild(n));
        } catch {}
        if (Array.isArray(page.tags)) {
            page.tags.forEach(t => {
                const name = typeof t === 'string' ? t : (t && t.name);
                if (name) this.appendMetaProperty('article:tag', name);
            });
        }

        // Twitter
        this.setMetaTag('twitter:card', image ? 'summary_large_image' : 'summary');
        this.setMetaTag('twitter:title', page.title || siteTitle);
        this.setMetaTag('twitter:description', description);
        if (image) this.setMetaTag('twitter:image', image);
        if (siteTwitter) this.setMetaTag('twitter:site', `@${siteTwitter}`);

        // JSON-LD Article
        const articleJson = {
            '@context': 'https://schema.org',
            '@type': 'Article',
            mainEntityOfPage: { '@type': 'WebPage', '@id': url },
            headline: page.title,
            description,
            datePublished: page.published_at ? new Date(page.published_at).toISOString().slice(0, 10) : undefined,
            dateModified: page.updated_at ? new Date(page.updated_at).toISOString().slice(0, 10) : undefined,
            image: image ? [image] : undefined,
            inLanguage: siteLocale,
            keywords,
            publisher: { '@type': 'Organization', '@id': `${(this.config.baseUrl || '').replace(/\/$/, '')}#org` },
            speakable: { '@type': 'SpeakableSpecification', cssSelector: ['article h1', 'article p'] }
        };
        // Clean undefined keys
        Object.keys(articleJson).forEach(k => articleJson[k] === undefined && delete articleJson[k]);
        this.injectJsonLd(this.seoScriptIds.article, articleJson);

        // JSON-LD Breadcrumbs
        this.injectJsonLd(this.seoScriptIds.breadcrumbs, this.buildBreadcrumbJsonLd(page));
    }

    showPageNotFound() {
        const contentElement = document.getElementById('wiki-content');
        if (contentElement) {
            contentElement.innerHTML = `
                <div class="wiki-not-found">
                    <h2>Page Not Found</h2>
                    <p>The requested wiki page could not be found.</p>
                    <a href="${this.config.baseUrl}/wiki/" class="wiki-nav-link-btn">
                        Return to Wiki Index
                    </a>
                </div>
            `;
        }
    }

    updateBreadcrumb() {
        const breadcrumbElement = document.getElementById('wiki-breadcrumb');
        if (!breadcrumbElement || !this.currentPage) return;

        const currentPage = this.wikiPages.find(page => page.url === this.currentPage);
        if (!currentPage) return;

        // Build breadcrumb from URL segments under /wiki/
        const base = (this.config.baseUrl || '').replace(/\/$/, '');
        const toSegments = (url) => {
            const abs = url.startsWith('http') ? url.replace(base, '') : url;
            const trimmed = abs.replace(/^\/?/, '/').replace(/\/$/, '/').replace(/^\/wiki\//, '').replace(/\/$/, '');
            return trimmed ? trimmed.split('/') : [];
        };
        const labelize = (s) => s.replace(/-/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
        const segments = toSegments(currentPage.url);

        const parts = [];
        parts.push(`<a href="${base}/wiki/">Wiki</a>`);
        let acc = [];
        for (let i = 0; i < segments.length - 1; i += 1) {
            acc.push(segments[i]);
            const seg = segments[i];
            parts.push('<span class="wiki-breadcrumb-separator">/</span>');
            parts.push(`<a href="${base}/wiki/tag/${encodeURIComponent(seg)}/">${labelize(seg)}</a>`);
        }
        if (segments.length > 0) {
            parts.push('<span class="wiki-breadcrumb-separator">/</span>');
        }
        parts.push(`<span>${currentPage.title}</span>`);
        breadcrumbElement.innerHTML = parts.join('');
    }

    updateNavigation() {
        // Update active state in navigation
        document.querySelectorAll('.wiki-tree-link').forEach(link => {
            link.classList.remove('active');
            if (link.getAttribute('href') === this.currentPage) {
                link.classList.add('active');
            }
        });
    }

    updatePrevNextLinks() {
        const currentIndex = this.wikiPages.findIndex(page => page.url === this.currentPage);
        if (currentIndex === -1) return;

        const prevElement = document.getElementById('wiki-nav-prev');
        const nextElement = document.getElementById('wiki-nav-next');

        if (prevElement) {
            if (currentIndex > 0) {
                const prevPage = this.wikiPages[currentIndex - 1];
                prevElement.innerHTML = `
                    <a href="${prevPage.url}" class="wiki-nav-link-btn prev">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="15,18 9,12 15,6"></polyline>
                        </svg>
                        ${prevPage.title}
                    </a>
                `;
            } else {
                prevElement.innerHTML = '';
            }
        }

        if (nextElement) {
            if (currentIndex < this.wikiPages.length - 1) {
                const nextPage = this.wikiPages[currentIndex + 1];
                nextElement.innerHTML = `
                    <a href="${nextPage.url}" class="wiki-nav-link-btn next">
                        ${nextPage.title}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="9,18 15,12 9,6"></polyline>
                        </svg>
                    </a>
                `;
            } else {
                nextElement.innerHTML = '';
            }
        }
    }
}

// Initialize markdown wiki system (supports late-loaded modules after DOMContentLoaded)
const initializeMarkdownWiki = () => {
    if (document.querySelector('.wiki-container') && window.wikiConfig?.dataUrl) {
        // Tear down any Ghost-backed WikiSystem if it ran first
        try {
            document.querySelectorAll('.wiki-tree').forEach((ul) => ul.remove());
        } catch {}
        new MarkdownWikiSystem();
    }
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeMarkdownWiki, { once: true });
} else {
    initializeMarkdownWiki();
}
